import { useTranslation } from 'react-i18next';
import './Conversations.css';
import { useConversations } from './useConversations';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, EmptyState, Pagination, useDeferredLoading } from '@anticatrade/shared';
import { Heading } from '../../base/Heading/Heading';

const Conversations = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();

	const { isLoading, conversations, count, page, setPage, limit } = useConversations();

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const onConversationClick = (id: number) => {
		navigate(`/conversations/${id}`);
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return date.toLocaleTimeString(i18n.language, {
				hour: '2-digit',
				minute: '2-digit',
			});
		} else if (date.toDateString() === yesterday.toDateString()) {
			return t('Yesterday');
		} else {
			return date.toLocaleDateString(i18n.language, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
			});
		}
	};

	let headingCount: ReactNode = null;
	if (!isLoading) {
		const start = (page - 1) * limit + 1;
		let end = start + limit - 1;
		if (end > count!) {
			end = count!;
		}
		headingCount = `(${start}-${end} ${t('Of')} ${count})`;
	}

	return (
		<div className='conversations'>
			{showContent ? (
				<div
					className={`conversations__wrapper${
						conversations.length === 0 ? ' conversations__wrapper--empty' : ''
					}`}
				>
					<Heading
						left={
							<div className='conversations__heading-title'>
								<h1 className='conversations__heading-title-text'>
									{t('Messages')}
								</h1>
								{conversations.length > 0 && (
									<span className='conversations__heading-count'>
										{headingCount}
									</span>
								)}
							</div>
						}
					/>
					{conversations.length === 0 ? (
						<div className='conversations__empty wrapped'>
							<EmptyState
								title={t('No messages yet')}
								description={t('Messages empty description')}
								icon='chat-right-text'
							/>
						</div>
					) : (
						<div className='conversations__content'>
							<div className='conversations__list wrapped'>
								{conversations.map((c) => (
									<div
										key={c.id}
										className={`conversations__item${
											c.unreadMessagesCount > 0
												? ' conversations__item--unread'
												: ''
										}`}
										onClick={() => onConversationClick(c.id)}
									>
										<div className='conversations__item-heading'>
											<div className='conversations__item-preview'>
												<div className='conversations__name-date'>
													<span className='conversations__buyer-name'>
														{c.buyerName}
													</span>
													<span className='conversations__date'>
														{' '}
														<span
															title={new Date(
																c.lastMessageAt,
															).toLocaleString(i18n.language)}
														>
															{formatDate(c.lastMessageAt)}
														</span>
													</span>
												</div>
												<div className='conversations__preview-count'>
													<span className='conversations__preview'>
														{c.lastMessageSender === 'merchant'
															? t('You')
															: c.lastMessageSenderName}
														: {c.lastMessagePreview}
													</span>
													{c.unreadMessagesCount > 0 && (
														<div className='conversations__count'>
															<span className='conversations__unread-badge'>
																{c.unreadMessagesCount > 99
																	? '99+'
																	: c.unreadMessagesCount}
															</span>
														</div>
													)}
												</div>
											</div>
										</div>
										{c.productID != null && (
											<div className='conversations__product'>
												{c.productImageFilename ? (
													<SlTooltip
														className='conversations__product-tooltip'
														content={c.productTitle}
														placement='left'
													>
														<span>
															<img
																src={`${
																	import.meta.env
																		.VITE_PRODUCT_IMAGE_URL
																}/${c.productID}/thumbnail-${
																	c.productImageFilename
																}.webp`}
															/>
														</span>
													</SlTooltip>
												) : (
													<span>-</span>
												)}
											</div>
										)}
										<div className='conversations__arrow'>
											<SlIcon name='chevron-right' />
										</div>
									</div>
								))}
							</div>
							<Pagination
								count={count!}
								limit={limit}
								page={page}
								setPage={setPage}
								t={t}
							/>
						</div>
					)}
				</div>
			) : (
				showSpinner && <Spinner />
			)}
		</div>
	);
};

export { Conversations };
